import { useEffect, useState, useCallback, memo } from 'react';
import { db } from "../config/Firebase";
import { collection, getDocs, doc, getDoc, query, limit, updateDoc } from 'firebase/firestore';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUser, FaGlasses, FaShoppingCart, FaChevronDown, FaSync } from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';

const productCollections = [
  "DesignerMetal&MetalFrame",
  "DesignerMetal_MetalFrame",
  "Accessories",
  "FrameLess",
  "FullFrame",
  "HalfFrame",
  "Kids-Frame",
  "Kids-Sunglass",
  "LDB",
  "SafetyGlassess",
  "Sunglassess",
  "Types"
];

const statusOptions = [
  "Order Confirmed",
  "Order Shipping",
  "Order Delivered"
];

const OrderItem = memo(({ order, productImages, updateProductStatus }) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
    hover: { scale: 1.02, boxShadow: "0 10px 20px rgba(0, 0, 0, 0.15)", transition: { duration: 0.3 } }
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover="hover"
      className="bg-white rounded-2xl shadow-md p-6 mb-6 border border-black"
    >
      <h3 className="text-xl font-bold text-black mb-4">{order.email || 'N/A'}</h3>
      <details className="mb-4">
        <summary className="flex items-center text-lg font-semibold text-black cursor-pointer hover:text-black/80">
          <FaUser className="mr-2 text-black" /> Customer Details
          <FaChevronDown className="ml-auto text-black" />
        </summary>
        <div className="mt-2 pl-4 text-black space-y-2">
          <p><span className="font-semibold">Name:</span> {order.customerDetails?.name || 'N/A'}</p>
          <p><span className="font-semibold">Address:</span> {order.customerDetails?.address || 'N/A'}</p>
          <p><span className="font-semibold">Phone:</span> {order.customerDetails?.phone || 'N/A'}</p>
          <p><span className="font-semibold">Pincode:</span> {order.customerDetails?.pincode || 'N/A'}</p>
        </div>
      </details>
      <details className="mb-4">
        <summary className="flex items-center text-lg font-semibold text-black cursor-pointer hover:text-black/80">
          <FaGlasses className="mr-2 text-black" /> Lens Option
          <FaChevronDown className="ml-auto text-black" />
        </summary>
        <div className="mt-2 pl-4 text-black space-y-2">
          <p><span className="font-semibold">ID:</span> {order.lensOption?.id || 'N/A'}</p>
          <p><span className="font-semibold">Name:</span> {order.lensOption?.name || 'N/A'}</p>
          <p><span className="font-semibold">lensOption:</span> {order.lensOption?.lensOptions || 'N/A'}</p>
          <p><span className="font-semibold">Left Lens Power:</span> {order.lensOption?.leftLensPower || 'N/A'}</p>
          <p><span className="font-semibold">Right Lens Power:</span> {order.lensOption?.rightLensPower || 'N/A'}</p>
        </div>
      </details>
      <details className="mb-4">
        <summary className="flex items-center text-lg font-semibold text-black cursor-pointer hover:text-black/80">
          <FaShoppingCart className="mr-2 text-black" /> Order Details
          <FaChevronDown className="ml-auto text-black" />
        </summary>
        <div className="mt-2 pl-4 text-black space-y-2">
          <p><span className="font-semibold">Payment ID:</span> {order.paymentId || 'N/A'}</p>
          <p><span className="font-semibold">Razorpay Order ID:</span> {order.razorpayOrderId || 'N/A'}</p>
          <p><span className="font-semibold">Timestamp:</span> {order.timestamp || 'N/A'}</p>
          <p><span className="font-semibold">Total Amount:</span> ${order.totalAmount || 'N/A'}</p>
        </div>
      </details>
      <h4 className="text-lg font-semibold text-black mb-3">Products</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {order.products && order.products.length > 0 ? (
          order.products.map((product, prodIndex) => {
            const productId = product.productID || product.productId;
            const imageUrl = productId ? productImages[productId] : null;

            return (
              <motion.div
                key={prodIndex}
                className="bg-white rounded-xl p-4 border border-black hover:bg-black/5 transition"
                whileHover={{ scale: 1.03 }}
              >
                <p className="text-black"><span className="font-semibold">Name:</span> {product.name || 'N/A'}</p>
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={product.name || 'Product'}
                    className="w-32 h-32 object-contain my-2 rounded-lg border border-black shadow-sm"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/150';
                      console.error('Failed to load image:', imageUrl);
                    }}
                  />
                ) : (
                  <p className="text-black my-2">Image: Not available</p>
                )}
                <p className="text-black"><span className="font-semibold">Cost:</span> ${product.cost || 'N/A'}</p>
                <p className="text-black"><span className="font-semibold">Frame Color:</span> {product.frameColor || 'N/A'}</p>
                <p className="text-black"><span className="font-semibold">Material:</span> {product.material || 'N/A'}</p>
                <p className="text-black"><span className="font-semibold">Product ID:</span> {productId || 'N/A'}</p>
                <p className="text-black"><span className="font-semibold">Shape:</span> {product.shape || 'N/A'}</p>
                <div className="flex items-center mt-3">
                  <label className="font-semibold text-black mr-2">Status:</label>
                  <select
                    value={product.status || 'Order Confirmed'}
                    onChange={(e) => updateProductStatus(order.email, order.razorpayOrderId, prodIndex, e.target.value)}
                    className="px-3 py-1.5 border border-black rounded-lg bg-white text-black hover:bg-black/10 focus:ring-2 focus:ring-black/50 outline-none transition"
                  >
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </motion.div>
            );
          })
        ) : (
          <p className="text-black">No products available</p>
        )}
      </div>
    </motion.div>
  );
});

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [productImages, setProductImages] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(
        query(collection(db, 'detailsorder'), limit(10))
      );
      const ordersData = [];

      querySnapshot.forEach((doc) => {
        const email = doc.id;
        const orderData = doc.data();

        if (orderData.orders) {
          const normalizedOrders = Array.isArray(orderData.orders)
            ? orderData.orders
            : [orderData.orders];

          normalizedOrders.forEach(order => {
            ordersData.push({
              email,
              ...order,
              products: Array.isArray(order.products)
                ? order.products.map(product => ({
                  ...product,
                  status: product.status || 'Order Confirmed'
                }))
                : order.productDetails
                  ? [{ ...order.productDetails, status: order.productDetails.status || 'Order Confirmed' }]
                  : [{ ...order.products, status: order.products?.status || 'Order Confirmed' }]
            });
          });
        }
      });

      ordersData.sort((a, b) => {
        const timestampA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timestampB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timestampB - timestampA;
      });

      setOrders(ordersData);

      const productIds = [...new Set(
        ordersData.flatMap(order =>
          order.products.map(product => product.productID || product.productId)
        ).filter(Boolean)
      )];

      const images = {};
      await Promise.all(
        productCollections.map(async (collectionName) => {
          const productPromises = productIds.map(async (productId) => {
            try {
              const productRef = doc(db, collectionName, productId);
              const productDoc = await getDoc(productRef);
              if (productDoc.exists()) {
                const data = productDoc.data();
                const imageUrl = [
                  data.ImageUrl1, data.ImageUrl2,
                  data.ImageUrl3, data.ImageUrl4,
                  data.imageUrl, data.image
                ].find(url => url && typeof url === 'string');
                if (imageUrl && !images[productId]) {
                  images[productId] = imageUrl;
                }
              }
            } catch (error) {
              console.error(`Error fetching ${productId} from ${collectionName}:`, error);
            }
          });
          await Promise.all(productPromises);
        })
      );

      setProductImages(images);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateProductStatus = useCallback(async (email, razorpayOrderId, productIndex, newStatus) => {
    try {
      const orderDocRef = doc(db, 'detailsorder', email);
      const orderDoc = await getDoc(orderDocRef);
      if (!orderDoc.exists()) {
        console.error('Order document not found for email:', email);
        return;
      }

      const orderData = orderDoc.data();
      const ordersArray = Array.isArray(orderData.orders) ? orderData.orders : [orderData.orders];

      const orderIndex = ordersArray.findIndex(order => order.razorpayOrderId === razorpayOrderId);
      if (orderIndex === -1) {
        console.error('Order not found for razorpayOrderId:', razorpayOrderId);
        return;
      }

      const products = Array.isArray(ordersArray[orderIndex].products)
        ? ordersArray[orderIndex].products
        : [ordersArray[orderIndex].productDetails || ordersArray[orderIndex].products];

      products[productIndex] = { ...products[productIndex], status: newStatus };
      ordersArray[orderIndex].products = products;

      await updateDoc(orderDocRef, { orders: ordersArray });

      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.email === email && order.razorpayOrderId === razorpayOrderId
            ? {
              ...order,
              products: order.products.map((product, idx) =>
                idx === productIndex ? { ...product, status: newStatus } : product
              )
            }
            : order
        )
      );

      const order = ordersArray[orderIndex];
      const product = products[productIndex];
      const customerDetails = order.customerDetails || {};
      const payload = {
        productId: product.productID || product.productId || 'N/A',
        finalCost: product.cost || order.totalAmount || '0',
        customerName: customerDetails.name || 'Customer',
        customerEmail: email || '',
        phoneNumber: customerDetails.phone || undefined,
        status: newStatus,
        emailType: 'statusUpdate'
      };

      const response = await axios.post('https://aramemail.outliersunited.com/send-email', payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      console.log('Email notification sent successfully:', response.data);
    } catch (error) {
      console.error('Error updating product status or sending email:', {
        message: error.message,
        response: error.response ? error.response.data : null,
        stack: error.stack
      });
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-3xl sm:text-4xl font-extrabold text-white bg-black py-4 px-6 rounded-lg shadow-md text-center mb-8"
        >
          Orders
        </motion.h1>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-7xl mx-auto"
        >
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <FaSync className="animate-spin text-4xl text-black" />
            </div>
          ) : orders.length === 0 ? (
            <p className="text-center text-xl text-black">No orders available.</p>
          ) : (
            orders.map((order, index) => (
              <OrderItem
                key={index}
                order={order}
                productImages={productImages}
                updateProductStatus={updateProductStatus}
              />
            ))
          )}
        </motion.div>
      </div>
      <Footer />
    </>
  );
}